import React, { useRef } from 'react';
import { Link } from 'react-router-dom';

import { trackLegacyEvent } from 'helpers/analytics';
import Recos from 'components/productdetail/Recos';
import marketplace from 'cfg/marketplace.json';
import type { SimilarStyles } from 'types/product';
import type { ProductStockData } from 'types/cloudCatalog';
import useMartyContext from 'hooks/useMartyContext';
import CloseIcon from 'components/common/IconsV2/CloseIcon';
import { Input } from 'components/common/Input';

// eslint-disable-next-line css-modules/no-unused-class
import css from 'styles/components/productdetail/outOfStockPopover.scss';

const { shortName } = marketplace;
const simLenLimit = 4;

interface Props {
  brandId: string;
  brandName: string;
  isSubmitted: boolean;
  onBrandNotifySubmit: any; // TODO type this when `Brand` types are finished
  onCloseClick: () => void;
  onShow: () => void;
  recos?: SimilarStyles;
  stock: ProductStockData | null;
}

/**
 * 6pm out of stock slide-in popover
 */
const OutOfStockPopover = ({ brandId, brandName, isSubmitted, onBrandNotifySubmit, onCloseClick, recos }: Props) => {
  const { testId } = useMartyContext();
  const emailAddressRef = useRef<HTMLInputElement>(null);

  const onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const emailAddress = emailAddressRef.current?.value;
    onBrandNotifySubmit({ brandId, brandName, emailAddress });
  };

  const onRecoClicked = () => {
    trackLegacyEvent('More-Like-This', '6PM.product-page-matinee', 'Sidebar-Image-Click');
  };

  const similarItems = recos && recos.results && recos.results.length ? recos.results.slice(0, simLenLimit) : [];
  const similarItemsAsFormattedJanusRecos = similarItems.map(item => ({
    ...item,
    productId: item.productId.toString(),
    link: '',
    recoName: '',
    thumbnailImageId: ''
  }));

  const additionalMelodyCardProductClasses = {
    brandName: css.melodyCardProductBrandName,
    container: css.melodyCardProductContainer,
    image: css.melodyCardProductImage,
    priceContainer: css.melodyCardProductPriceContainer,
    textContainer: css.melodyCardProductTextContainer
  };

  return (
    <div data-test-id={testId('oosPopover')} className={css.oosPopover}>
      <h1>Sorry, this is out of stock. You just missed it.</h1>
      <div className={css.oosMlt}>
        <h2>But check out these similar styles available:</h2>
        <div className={css.recosWrapper}>
          <Recos
            additionalMelodyCardProductClasses={additionalMelodyCardProductClasses}
            noBackground={true}
            onRecoClicked={onRecoClicked}
            recos={similarItemsAsFormattedJanusRecos}
            recoType="oosRecos"
            showTitle={false}
          />
        </div>
      </div>
      <div className={css.oosNotifyBox}>
        <form method="post" onSubmit={onFormSubmit}>
          {!isSubmitted && <p> Sign up to receive {brandName} deals as they become available!</p>}
          {isSubmitted && <p className={css.successMessage}> You are now signed up to receive the latest {brandName} notifications!</p>}
          {!isSubmitted && (
            <div>
              <Input
                ref={emailAddressRef}
                className={css.emailInput}
                type="email"
                required={true}
                name="emailSubscription.emailAddress"
                defaultValue=""
                placeholder="Email Address"
                data-test-id={testId('emailAddress')}
              />
              <input type="hidden" value={brandId} name="brandId" />
              <input type="hidden" value={brandName} name="brandName" />
              <button
                id="oosSubmit"
                type="submit"
                data-track-action="Product-Page"
                data-track-label="OOS"
                data-track-value="Notify-Me-Submit"
                data-test-id={testId('oosSubmit')}
              >
                Notify Me
              </button>
            </div>
          )}
        </form>
      </div>
      <p className={css.privacyMessage}>
        {shortName} will never sell your information. Read our <Link to="/c/privacy-policy">Privacy Policy.</Link>
      </p>
      <CloseIcon size={24} onClick={onCloseClick} data-test-id={testId('close')} />
    </div>
  );
};

export default OutOfStockPopover;
